import React, { useEffect, useRef, useState } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { ToastContainer } from 'react-toastify';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import Navbar from './components/Navbar';
import LoginPage from './pages/auth/LoginPage';
import SignupPage from './pages/auth/SignupPage';
import SsoCallbackPage from './pages/auth/SsoCallbackPage';
import MainLandingPage from './pages/main/MainLandingPage';
import HomePage from './pages/ranking2/RankingPage';
import ComparePage from './pages/compare/ComparePage';
import ComplexComparePage from './pages/compare/ComplexComparePage';
import SubChartPopupPage from './pages/compare/SubChartPopupPage';
import KBDataPage from './pages/kb/KBDataPage';
import RegionTab from './pages/ranking2/RegionTab';
import PricingPage from './pages/pricing/PricingPage';
import MyPage from './pages/mypage/MyPage';
import AdminPage from './pages/admin/AdminPage';
import NoticePage from './pages/notice/NoticePage';
import CustomerServicePage from './pages/support/CustomerServicePage';
import ApartmentDetailPage from './pages/ApartmentDetailPage';
import RankingPage from './pages/ranking/RankingPage';
import RankingComparePopupPage from './pages/ranking/RankingComparePopupPage';
import FavoritesPage from './pages/favorite/FavoritesPage';
import { getNoticeList } from './lib/api/notice-support-api';
import 'react-toastify/dist/ReactToastify.css';
import './App.css';

function AppContent() {
  const location = useLocation();
  const { isLoggedIn, logout, loading } = useAuth();
  const isAuthPage =
    location.pathname === '/' ||
    location.pathname === '/login' ||
    location.pathname === '/signup' ||
    location.pathname === '/sso/callback' ||
    location.pathname === '/compare/sub-chart-popup' ||
    location.pathname === '/ranking/compare-popup';
  const [popupNotice, setPopupNotice] = useState(null);
  const popupCheckedRef = useRef(false);

  useEffect(() => {
    if (!isLoggedIn) {
      popupCheckedRef.current = false;
      setPopupNotice(null);
      return;
    }

    if (popupCheckedRef.current) {
      return;
    }

    const loadPopupNotice = async () => {
      try {
        const response = await getNoticeList();
        const notices = response?.data || [];
        const now = new Date();

        const candidate = notices.find((notice) => {
          if (!notice?.popup?.enabled) {
            return false;
          }

          const startAt = notice.popup.startAt ? new Date(notice.popup.startAt) : null;
          const endAt = notice.popup.endAt ? new Date(notice.popup.endAt) : null;
          const isWithinPeriod = (!startAt || now >= startAt) && (!endAt || now <= endAt);
          if (!isWithinPeriod) {
            return false;
          }

          const hiddenUntil = localStorage.getItem(`notice-popup-hide-until-${notice.id}`);
          return !hiddenUntil || now > new Date(hiddenUntil);
        });

        setPopupNotice(candidate || null);
      } catch (error) {
        console.error('공지 팝업 로드 실패:', error);
      } finally {
        popupCheckedRef.current = true;
      }
    };

    loadPopupNotice();
  }, [isLoggedIn]);

  const closePopup = () => {
    setPopupNotice(null);
  };

  const suppressPopup = (days) => {
    if (!popupNotice) {
      return;
    }

    const now = new Date();
    const hiddenUntil = new Date(now);
    if (days === 0) {
      // 오늘 보지 않음: 오늘 자정까지
      hiddenUntil.setHours(23, 59, 59, 999);
    } else {
      // N일 보지 않음: N일 후 자정까지
      hiddenUntil.setDate(hiddenUntil.getDate() + days);
      hiddenUntil.setHours(23, 59, 59, 999);
    }
    localStorage.setItem(`notice-popup-hide-until-${popupNotice.id}`, hiddenUntil.toISOString());
    closePopup();
  };

  if (loading) {
    return <div className="loading">로딩 중...</div>;
  }

  return (
    <div className="app">
      {!isAuthPage && <Navbar isLoggedIn={isLoggedIn} onLogout={logout} />}
      <div className="app-content">
        <Routes>
          <Route path="/" element={<MainLandingPage />} />
          <Route path="/login" element={<LoginPage />} />
          <Route path="/signup" element={<SignupPage />} />
          <Route path="/sso/callback" element={<SsoCallbackPage />} />
          <Route path="/home" element={<HomePage />} />
          <Route path="/ranking" element={<RankingPage />} />
          <Route path="/ranking/compare-popup" element={<RankingComparePopupPage />} />
          <Route path="/compare" element={<ComparePage />} />
          <Route path="/complex-compare" element={<ComplexComparePage />} />
          <Route path="/compare/sub-chart-popup" element={<SubChartPopupPage />} />
          <Route path="/kb-data" element={<KBDataPage />} />
          <Route path="/region" element={<RegionTab />} />
          <Route path="/pricing" element={<PricingPage />} />
          <Route path="/favorites" element={isLoggedIn ? <FavoritesPage /> : <Navigate to="/login" />} />
          <Route path="/mypage" element={isLoggedIn ? <MyPage /> : <Navigate to="/login" />} />
          <Route path="/admin" element={isLoggedIn ? <AdminPage /> : <Navigate to="/login" />} />
          <Route path="/notice" element={<NoticePage />} />
          <Route path="/notice/:id" element={<NoticePage />} />
          <Route path="/customer-service" element={<CustomerServicePage />} />
          <Route path="/customer-service/:id" element={<CustomerServicePage />} />
          <Route path="/apartment/:id" element={<ApartmentDetailPage />} />
        </Routes>
      </div>

      {popupNotice && (
        <div className="notice-popup-overlay" role="dialog" aria-modal="true" aria-label="공지 팝업">
          <div
            className="notice-popup-modal"
            style={{
              width: `min(${popupNotice.popup.width || 520}px, calc(100vw - 32px))`,
              minHeight: `min(${popupNotice.popup.height || 420}px, calc(100vh - 96px))`,
            }}
          >
            <div className="notice-popup-header">
              <h3>{popupNotice.title}</h3>
              <button type="button" className="notice-popup-close" onClick={closePopup}>
                닫기
              </button>
            </div>
            <div className="notice-popup-body">
              {popupNotice.content}
            </div>
            <div className="notice-popup-footer">
              <button type="button" className="notice-popup-btn ghost" onClick={() => suppressPopup(0)}>
                오늘 보지 않음
              </button>
              <button type="button" className="notice-popup-btn ghost" onClick={() => suppressPopup(7)}>
                일주일 보지 않음
              </button>
              <button type="button" className="notice-popup-btn primary" onClick={closePopup}>
                확인
              </button>
            </div>
          </div>
        </div>
      )}

      <ToastContainer
        position="top-center"
        autoClose={1800}
        hideProgressBar
        newestOnTop
        closeOnClick
        pauseOnFocusLoss={false}
        pauseOnHover
      />
    </div>
  );
}

function App() {
  return (
    <Router>
      <AuthProvider>
        <AppContent />
      </AuthProvider>
    </Router>
  );
}

export default App;
